import React, { useEffect, useState, useCallback } from 'react';
import { ChevronLeft, ChevronRight, FileText, FileType, FileSpreadsheet, File, Music, Video } from 'lucide-react';
import { useFileApi } from '../../hooks/useFileApi';
import { FileViewer } from './FileViewer';

interface ImageCarouselProps {
  fileIds: string[];
  mainFileId?: string;
  autoPlay?: boolean;
  autoPlayInterval?: number;
}

interface FileData {
  fileId: string;
  url: string;
  fileName?: string;
  contentType?: string;
  status?: string;
  isLoading: boolean;
  error: boolean;
}

// File type detection utilities
const getFileType = (contentType: string = '', fileName: string = ''): string => {
  const ext = fileName.split('.').pop()?.toLowerCase();
  const lowerContentType = contentType.toLowerCase();

  // Check content type first, then fallback to extension
  if (lowerContentType.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif'].includes(ext || '')) {
    return 'image';
  }
  if (lowerContentType.includes('pdf') || ext === 'pdf') {
    return 'pdf';
  }
  if (lowerContentType.startsWith('video/') || ['mp4', 'webm', 'ogg', 'mov', 'avi'].includes(ext || '')) {
    return 'video';
  }
  if (lowerContentType.startsWith('audio/') || ['mp3', 'wav', 'ogg', 'aac'].includes(ext || '')) {
    return 'audio';
  }
  if (lowerContentType.includes('word') || lowerContentType.includes('msword') || ['doc', 'docx'].includes(ext || '')) {
    return 'word';
  }
  if (lowerContentType.includes('excel') || lowerContentType.includes('spreadsheet') || ['xls', 'xlsx'].includes(ext || '')) {
    return 'excel';
  }
  if (lowerContentType.includes('text') || ['txt', 'csv', 'log'].includes(ext || '')) {
    return 'text';
  }
  return 'unknown';
};

const getFileIcon = (type: string, size: string = 'w-6 h-6') => {
  switch (type) {
    case 'pdf':
      return <FileText className={`${size} text-red-500`} />;
    case 'word':
      return <FileType className={`${size} text-blue-500`} />;
    case 'excel':
      return <FileSpreadsheet className={`${size} text-green-600`} />;
    case 'video':
      return <Video className={`${size} text-purple-500`} />;
    case 'audio':
      return <Music className={`${size} text-pink-500`} />;
    case 'text':
      return <FileText className={`${size} text-gray-500`} />;
    default:
      return <File className={`${size} text-gray-500`} />;
  }
};

const ImageCarousel: React.FC<ImageCarouselProps> = ({
  fileIds,
  mainFileId,
  autoPlay = true,
  autoPlayInterval = 7000,
}) => {
  const { getFileUrl } = useFileApi();
  const [files, setFiles] = useState<Map<string, FileData>>(new Map());
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [viewingFile, setViewingFile] = useState<{ name: string; url: string } | null>(null);

  // Sort fileIds to show main image first
  const sortedFileIds = React.useMemo(() => {
    if (!mainFileId) return fileIds;
    const filtered = fileIds.filter(id => id !== mainFileId);
    return [mainFileId, ...filtered];
  }, [fileIds, mainFileId]);

  // Load file URL for a specific fileId
  const loadFileUrl = useCallback(async (fileId: string) => {
    if (!fileId) return;

    // Set loading state using functional update to avoid dependency on files
    setFiles(prev => {
      if (prev.has(fileId)) return prev; // Skip if already loaded
      const newMap = new Map(prev);
      newMap.set(fileId, {
        fileId,
        url: '',
        isLoading: true,
        error: false,
      });
      return newMap;
    });

    try {
      const response = await getFileUrl(fileId);
      if (response.success && response.data && response.data.url) {
        const { url, fileName, contentType, status } = response.data;
        setFiles(prev => {
          const newMap = new Map(prev);
          newMap.set(fileId, {
            fileId,
            url,
            fileName,
            contentType,
            status,
            isLoading: false,
            error: false,
          });
          return newMap;
        });
      } else {
        throw new Error('Failed to load file URL');
      }
    } catch (error) {
      console.error('Error loading file:', error);
      setFiles(prev => {
        const newMap = new Map(prev);
        newMap.set(fileId, {
          fileId,
          url: '',
          isLoading: false,
          error: true,
        });
        return newMap;
      });
    }
  }, [getFileUrl]);

  // Preload all files on mount
  useEffect(() => {
    if (sortedFileIds.length === 0) return;

    sortedFileIds.forEach(fileId => {
      loadFileUrl(fileId);
    });
    // loadFileUrl is stable (uses functional updates), so we only depend on sortedFileIds
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedFileIds]);

  // Auto-play functionality - only cycle through images
  useEffect(() => {
    if (!autoPlay || isPaused || sortedFileIds.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex(prev => {
        // Find next image index (skip non-images)
        let nextIndex = (prev + 1) % sortedFileIds.length;
        let attempts = 0;
        
        // Try to find next image, but don't loop forever
        while (attempts < sortedFileIds.length) {
          const nextFileId = sortedFileIds[nextIndex];
          const nextFile = files.get(nextFileId);
          const fileType = getFileType(nextFile?.contentType || '', nextFile?.fileName || '');
          
          if (fileType === 'image') {
            return nextIndex;
          }
          
          nextIndex = (nextIndex + 1) % sortedFileIds.length;
          attempts++;
        }
        
        // If no images found, stay at current index
        return prev;
      });
    }, autoPlayInterval);

    return () => clearInterval(interval);
    // files Map recreates on updates, but we read it in the interval closure
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPlay, isPaused, sortedFileIds.length, autoPlayInterval]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        handlePrevious();
      } else if (e.key === 'ArrowRight') {
        handleNext();
      } else if (e.key === 'Enter') {
        // Open FileViewer for non-image files
        const currentFileId = sortedFileIds[currentIndex];
        const currentFile = files.get(currentFileId);
        const fileType = getFileType(currentFile?.contentType || '', currentFile?.fileName || '');
        
        if (fileType !== 'image' && currentFile?.url && currentFile?.fileName) {
          handleNonImageClick();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // files Map recreates on updates, but we read it in the event handler closure
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, sortedFileIds.length]);

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % sortedFileIds.length);
  };

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + sortedFileIds.length) % sortedFileIds.length);
  };

  const handleThumbnailClick = (index: number) => {
    setCurrentIndex(index);
  };

  const handleNonImageClick = () => {
    const currentFileId = sortedFileIds[currentIndex];
    const currentFile = files.get(currentFileId);
    
    if (currentFile && currentFile.url && currentFile.fileName) {
      setViewingFile({
        name: currentFile.fileName,
        url: currentFile.url,
      });
    }
  };

  const handleMouseEnter = () => {
    if (autoPlay) setIsPaused(true);
  };

  const handleMouseLeave = () => {
    if (autoPlay) setIsPaused(false);
  };

  // Empty state
  if (sortedFileIds.length === 0) {
    return (
      <div className="w-full py-12 text-center text-gray-500">
        <p>No files available</p>
      </div>
    );
  }

  const currentFileId = sortedFileIds[currentIndex];
  const currentFile = files.get(currentFileId);
  const showNavigation = sortedFileIds.length > 1;
  const fileType = getFileType(currentFile?.contentType || '', currentFile?.fileName || '');
  const isImage = fileType === 'image';
  const isProcessing = currentFile?.status === 'Processing' || currentFile?.status === 'Queued';
  const isFailed = currentFile?.status === 'Failed' || currentFile?.status === 'Quarantined';
  
  // Get file type label for ARIA
  const getFileTypeLabel = (type: string) => {
    switch (type) {
      case 'pdf': return 'PDF';
      case 'word': return 'Word document';
      case 'excel': return 'Excel spreadsheet';
      case 'video': return 'video';
      case 'audio': return 'audio';
      case 'image': return 'image';
      default: return 'file';
    }
  };

  return (
    <>
      <div className="w-full space-y-4">
        {/* Main file display */}
        <div
          className="relative w-full rounded-xl overflow-hidden"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          style={{ paddingBottom: '60%' }} // Slightly taller aspect ratio for better display
        >
          <div className="absolute inset-0 flex items-center justify-center p-4">
            {currentFile?.isLoading && (
              <div className="flex flex-col items-center gap-2">
                <div className="w-8 h-8 border-4 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
                <span className="text-sm text-gray-500">Loading...</span>
              </div>
            )}
            
            {isProcessing && !currentFile?.isLoading && (
              <div className="text-center">
                <div className="w-12 h-12 border-4 border-orange-300 border-t-orange-500 rounded-full animate-spin mx-auto mb-3" />
                <p className="font-semibold text-gray-700">Processing...</p>
                <p className="text-sm text-gray-500">File is being scanned for security</p>
              </div>
            )}
            
            {isFailed && !currentFile?.isLoading && (
              <div className="text-center text-red-500">
                <p className="font-semibold">Failed to process file</p>
                <p className="text-sm">This file could not be processed</p>
              </div>
            )}
            
            {currentFile?.error && !currentFile?.isLoading && (
              <div className="text-center text-red-500">
                <p className="font-semibold">Failed to load file</p>
                <p className="text-sm">Please try again later</p>
              </div>
            )}
            
            {/* Image display */}
            {isImage && currentFile?.url && !currentFile.isLoading && !currentFile.error && !isProcessing && !isFailed && (
              <img
                src={currentFile.url}
                alt={currentFile.fileName || `Image ${currentIndex + 1}`}
                className="absolute inset-0 w-full h-full object-contain transition-opacity duration-300"
                style={{ opacity: 1 }}
              />
            )}
            
            {/* Non-image display - show icon and click to view */}
            {!isImage && currentFile?.url && !currentFile.isLoading && !currentFile.error && !isProcessing && !isFailed && (
              <div 
                className="flex flex-col items-center gap-4 cursor-pointer hover:scale-105 transition-transform duration-200"
                onClick={handleNonImageClick}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && handleNonImageClick()}
              >
                {getFileIcon(fileType, 'w-20 h-20 md:w-32 md:h-32')}
                <div className="flex flex-col items-center text-center">
                  <p className="font-semibold text-gray-700 mb-1">{currentFile.fileName}</p>
                  <p className="w-40 text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">Click to view</p>
                </div>
              </div>
            )}
          </div>

          {/* Previous/Next buttons */}
          {showNavigation && !currentFile?.isLoading && (
            <>
              <button
                onClick={handlePrevious}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/95 hover:bg-slate-100 text-gray-400 rounded-full p-3 shadow-2xl transition-all duration-200 hover:scale-110 hover:shadow-blue-200 focus:outline-none focus:ring-2 focus:ring-gray-400 backdrop-blur-sm"
                aria-label={`Previous ${getFileTypeLabel(fileType)}`}
              >
                <ChevronLeft className="w-7 h-7" />
              </button>
              
              <button
                onClick={handleNext}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/95 hover:bg-slate-100 text-gray-400 rounded-full p-3 shadow-2xl transition-all duration-200 hover:scale-110 hover:shadow-blue-200 focus:outline-none focus:ring-2 focus:ring-gray-400 backdrop-blur-sm"
                aria-label={`Next ${getFileTypeLabel(fileType)}`}
              >
                <ChevronRight className="w-7 h-7" />
              </button>
            </>
          )}

          {/* File counter */}
          {showNavigation && (
            <div className="absolute bottom-6 right-6 bg-black/70 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium shadow-lg">
              {currentIndex + 1} / {sortedFileIds.length}
            </div>
          )}
        </div>

        {/* Thumbnail strip */}
        {showNavigation && (
          <div className="flex gap-2 md:gap-3 overflow-x-auto p-3 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
            {sortedFileIds.map((fileId, index) => {
              const fileData = files.get(fileId);
              const isActive = index === currentIndex;
              const thumbFileType = getFileType(fileData?.contentType || '', fileData?.fileName || '');
              const isThumbImage = thumbFileType === 'image';

              return (
                <button
                  key={fileId}
                  onClick={() => handleThumbnailClick(index)}
                  className={`flex-shrink-0 w-16 h-16 md:w-24 md:h-24 rounded-lg overflow-hidden transition-all duration-200 shadow-md ${
                    isActive
                      ? 'ring-2 ring-gray-400 scale-105'
                      : 'ring-2 ring-gray-200'
                  }`}
                  aria-label={`View ${getFileTypeLabel(thumbFileType)} ${index + 1}`}
                >
                  <div className="w-full h-full bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center relative">
                    {fileData?.url && isThumbImage ? (
                      <>
                        <img
                          src={fileData.url}
                          alt={`Thumbnail ${index + 1}`}
                          className="w-full h-full object-cover transition-transform duration-200 hover:scale-110"
                        />
                      </>
                    ) : fileData?.url && !isThumbImage ? (
                      <>
                        {/* Show file type icon for non-images */}
                        {getFileIcon(thumbFileType, 'w-8 h-8')}
                        {/* Small badge in corner to indicate file type */}
                        <div className="absolute bottom-1 right-1 bg-white/90 rounded px-1 py-0.5">
                          <span className="text-[8px] font-bold text-gray-600 uppercase">
                            {thumbFileType === 'word' ? 'DOC' : thumbFileType === 'excel' ? 'XLS' : thumbFileType.substring(0, 3)}
                          </span>
                        </div>
                      </>
                    ) : fileData?.isLoading ? (
                      <div className="w-5 h-5 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                    ) : (
                      <span className="text-xs text-gray-400 font-medium">Error</span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
      
      {/* FileViewer Modal */}
      {viewingFile && (
        <FileViewer
          fileName={viewingFile.name}
          sasUrl={viewingFile.url}
          onClose={() => setViewingFile(null)}
        />
      )}
    </>
  );
};

export default ImageCarousel;
